// apps/patient/src/pages/settings/MyLocations.tsx

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MapPin,
  Plus,
  Edit2,
  Trash2,
  Star,
  ChevronRight,
  Dumbbell,
  Home,
  Building2,
  Trees,
  Hospital,
  MoreHorizontal
} from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { patientApiService } from '../../services/api';
import { LocationForm } from '../../components/locations/LocationForm';
import { PatientLocation, PatientLocationType } from '@prism-ehr/shared';
import { LOCATION_TYPE_ICONS, LOCATION_TYPE_LABELS } from '../../constants/equipment';

export function MyLocations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const [locations, setLocations] = useState<PatientLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<PatientLocation | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadLocations();
  }, []);

  // If coming from a returnTo flow, auto-open the form if no locations exist
  useEffect(() => {
    if (returnTo && !loading && locations.length === 0) {
      setShowForm(true);
    }
  }, [returnTo, loading, locations.length]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await patientApiService.getMyLocations();
      setLocations(data || []);
    } catch (err: any) {
      console.error('Failed to load locations:', err);
      setError('Failed to load locations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (locationData: Partial<PatientLocation>) => {
    try {
      if (editingLocation) {
        await patientApiService.updateLocation(editingLocation.id, locationData);
      } else {
        await patientApiService.createLocation(locationData);
      }
      await loadLocations();
      setShowForm(false);
      setEditingLocation(null);

      // If we came from a returnTo flow, navigate back
      if (returnTo) {
        navigate(returnTo);
      }
    } catch (err: any) {
      console.error('Failed to save location:', err);
      throw err;
    }
  };

  const handleDelete = async (locationId: string) => {
    if (!confirm('Are you sure you want to delete this location?')) return;

    try {
      setDeletingId(locationId);
      await patientApiService.deleteLocation(locationId);
      await loadLocations();
    } catch (err: any) {
      console.error('Failed to delete location:', err);
      setError('Failed to delete location. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetDefault = async (locationId: string) => {
    try {
      await patientApiService.setDefaultLocation(locationId);
      await loadLocations();
    } catch (err: any) {
      console.error('Failed to set default:', err);
      setError('Failed to set default location.');
    }
  };

  const getLocationIcon = (type: PatientLocationType) => {
    switch (type) {
      case 'home':
        return <Home className="h-5 w-5" />;
      case 'commercial_gym':
        return <Dumbbell className="h-5 w-5" />;
      case 'workplace':
        return <Building2 className="h-5 w-5" />;
      case 'outdoor':
        return <Trees className="h-5 w-5" />;
      case 'clinic':
        return <Hospital className="h-5 w-5" />;
      default:
        return <MapPin className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 pt-6">
          <PageHeader title="My Locations" showBack />
        </div>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <PageHeader
          title="My Locations"
          showBack
          actions={
            <button
              onClick={() => {
                setEditingLocation(null);
                setShowForm(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Add Location
            </button>
          }
        />
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Return to Workout Banner */}
        {returnTo && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              Add a location with your available equipment, then you'll be returned to your workout.
            </p>
          </div>
        )}

        {/* Info Banner */}
        {!returnTo && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              Add your workout locations with their available equipment. When you start a workout,
              we'll suggest exercise substitutions based on what's available.
            </p>
          </div>
        )}

        {/* Locations List */}
        {locations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No locations yet</h3>
            <p className="text-gray-500 mb-6">
              Add your first workout location to get equipment-aware workout suggestions.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              <Plus className="h-5 w-5" />
              Add Your First Location
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {locations.map((location) => (
              <div
                key={location.id}
                className={`bg-white rounded-lg shadow-sm p-4 border-2 transition-colors ${
                  location.is_default ? 'border-primary' : 'border-transparent'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      location.is_default ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {getLocationIcon(location.location_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">{location.name}</h3>
                        {location.is_default && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {LOCATION_TYPE_LABELS[location.location_type] || 'Location'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="font-medium">{location.equipment.length}</span> equipment items
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!location.is_default && (
                      <button
                        onClick={() => handleSetDefault(location.id)}
                        className="p-2 text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 rounded-lg transition-colors"
                        title="Set as default"
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setEditingLocation(location);
                        setShowForm(true);
                      }}
                      className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(location.id)}
                      disabled={deletingId === location.id}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Equipment Preview */}
                {location.equipment.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex flex-wrap gap-1.5">
                      {location.equipment.slice(0, 5).map((eq) => (
                        <span
                          key={eq}
                          className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full"
                        >
                          {eq}
                        </span>
                      ))}
                      {location.equipment.length > 5 && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">
                          +{location.equipment.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Location Form Modal */}
      {showForm && (
        <LocationForm
          location={editingLocation}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditingLocation(null);
          }}
        />
      )}
    </div>
  );
}
